import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import SearchableSelect from '../components/SearchableSelect';
import { useFormDirty } from '../hooks/useFormDirty';
import { useRoleOptions } from '../hooks/useRoleOptions';
import { formatCurrency as fmtCur } from '../utils/currency';
import { formatDate } from '../utils/date';
import CostSheet from '../components/CostSheet';
import ContractLineItemsEditor from '../components/ContractLineItemsEditor';

const CONTRACT_STATUSES = ['draft', 'active', 'completed', 'expired', 'terminated'];
const CONTRACT_TYPES = ['Fixed Price', 'T&M', 'Milestone', 'Mixed', 'AMS', 'Staff Augmentation'];
const ENGAGEMENT_MODELS = ['FP', 'T&M', 'Milestone', 'Mixed'];
const SOW_STATUSES = ['draft', 'active', 'completed', 'on_hold'];
const CO_STATUSES = ['pending', 'approved', 'rejected'];
const SLA_PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const AMENDMENT_STATUSES = ['draft', 'active', 'superseded'];
const SIGNOFF_STATUSES = ['pending', 'signed', 'rejected'];

const STATUS_BADGES = {
  draft: 'badge-gray', active: 'badge-green', completed: 'badge-blue',
  expired: 'badge-yellow', terminated: 'badge-red', pending: 'badge-yellow',
  approved: 'badge-green', rejected: 'badge-red', on_hold: 'badge-yellow',
  signed: 'badge-green', superseded: 'badge-gray'
};
const MODEL_BADGES = { FP: 'badge-blue', 'T&M': 'badge-purple', Milestone: 'badge-teal', Mixed: 'badge-yellow' };

const EMPTY_CONTRACT = {
  contract_number: '', customer_id: '', opportunity_id: '', estimate_id: '', proposal_id: '', project_id: '',
  type: '', total_value: '', negotiated_value: '', currency: 'INR',
  start_date: '', end_date: '', status: 'draft',
  document_url: '', po_number: '', po_document_url: '', contract_document_url: '',
  billing_terms: '', payment_terms: ''
};

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_CONTRACT });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Detail view
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [lineItems, setLineItems] = useState([]);
  const [sows, setSows] = useState([]);
  const [amendments, setAmendments] = useState([]);
  const [signoffs, setSignoffs] = useState([]);

  // SOW detail
  const [selectedSow, setSelectedSow] = useState(null);
  const [changeOrders, setChangeOrders] = useState([]);
  const [slaDefinitions, setSlaDefinitions] = useState([]);

  // Sub-modals
  const [subModal, setSubModal] = useState(null); // 'lineItem', 'sow', 'changeOrder', 'sla', 'amendment', 'signoff'
  const [subEditing, setSubEditing] = useState(null);
  const [subForm, setSubForm] = useState({});

  // Unsaved-changes tracking
  const { dirty, markDirty, resetDirty, confirmIfDirty } = useFormDirty();
  const { dirty: subDirty, markDirty: markSubDirty, resetDirty: resetSubDirty } = useFormDirty();
  // Canonical consulting roles (metadata_lookups category='role')
  const { roles: ROLE_OPTIONS } = useRoleOptions();

  const fetchContracts = useCallback(async () => {
    try {
      const [cRes, custRes, oppRes, estRes, projRes] = await Promise.all([
        apiFetch('/api/contracts'),
        apiFetch('/api/customers?limit=200'),
        apiFetch('/api/crm/opportunities?limit=200'),
        apiFetch('/api/estimates/effort-estimates'),
        apiFetch('/api/projects?limit=500')
      ]);
      if (cRes.ok) { const d = await cRes.json(); setContracts(d.contracts || []); }
      if (custRes.ok) { const d = await custRes.json(); setCustomers(d.customers || d.data || []); }
      if (oppRes.ok) { const d = await oppRes.json(); setOpportunities(d.opportunities || []); }
      if (estRes.ok) { const d = await estRes.json(); setEstimates(d.estimates || d.data || []); }
      if (projRes.ok) { const d = await projRes.json(); setProjects(d.projects || []); }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const fmtCurrency = (v) => fmtCur(v);
  const fmtDate = (v) => formatDate(v, '--');

  // ---- Contract CRUD ----
  /** Navigate to the full-page editor. The old modal is kept below for legacy
   *  fallback but +New and Edit now go to /contracts/new and /contracts/:id/edit. */
  function openCreate() { window.location.hash = '#/contracts/new'; }
  function openEditPage(c) { window.location.hash = `#/contracts/${c.id}/edit`; }
  function openViewPage(c) { window.location.hash = `#/contracts/${c.id}/view`; }
  function openEdit(c) {
    setEditing(c);
    setForm({
      contract_number: c.contract_number || '', customer_id: c.customer_id || '',
      opportunity_id: c.opportunity_id || '', estimate_id: c.estimate_id || '',
      proposal_id: c.proposal_id || '', project_id: c.project_id || '',
      type: c.type || '', total_value: c.total_value || '', negotiated_value: c.negotiated_value || '',
      currency: c.currency || 'INR', start_date: c.start_date ? c.start_date.substring(0, 10) : '',
      end_date: c.end_date ? c.end_date.substring(0, 10) : '', status: c.status || 'draft',
      document_url: c.document_url || '', po_number: c.po_number || '',
      po_document_url: c.po_document_url || '', contract_document_url: c.contract_document_url || '',
      billing_terms: c.billing_terms || '', payment_terms: c.payment_terms || ''
    });
    setErrors({}); resetDirty(); setModalOpen(true);
  }

  async function handleSave() {
    if (!form.contract_number.trim()) { setErrors({ contract_number: 'Contract number is required' }); return; }
    setSaving(true);
    const body = {
      contractNumber: form.contract_number, customerId: form.customer_id || null,
      opportunityId: form.opportunity_id || null, type: form.type || null,
      totalValue: form.total_value || 0, currency: form.currency,
      startDate: form.start_date || null, endDate: form.end_date || null,
      status: form.status, documentUrl: form.document_url || null,
      estimateId: form.estimate_id || null, proposalId: form.proposal_id || null,
      projectId: form.project_id || null, negotiatedValue: form.negotiated_value || null,
      poNumber: form.po_number || null, poDocumentUrl: form.po_document_url || null,
      contractDocumentUrl: form.contract_document_url || null,
      billingTerms: form.billing_terms || null, paymentTerms: form.payment_terms || null
    };
    const url = editing ? `/api/contracts/${editing.id}` : '/api/contracts';
    const method = editing ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) { resetDirty(); setModalOpen(false); fetchContracts(); }
      else { const d = await res.json(); setErrors({ _general: d.error || 'Failed to save' }); }
    } catch (e) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function handleDelete(c) {
    if (!window.confirm(`Delete contract "${c.contract_number}"?`)) return;
    try { await apiFetch(`/api/contracts/${c.id}`, { method: 'DELETE' }); fetchContracts(); } catch (e) { /* ignore */ }
  }

  // ---- Detail view ----
  async function openDetail(c) {
    setSelected(c);
    setDetailTab('overview');
    setSelectedSow(null);
    await loadDetailData(c.id);
  }

  async function loadDetailData(contractId) {
    try {
      const [liRes, sowRes, amRes, sigRes] = await Promise.all([
        apiFetch(`/api/contracts/${contractId}/line-items`),
        apiFetch(`/api/contracts/${contractId}/sows`),
        apiFetch(`/api/contracts/${contractId}/amendments`),
        apiFetch(`/api/contracts/signoffs?entityType=contract&entityId=${contractId}`)
      ]);
      if (liRes.ok) { const d = await liRes.json(); setLineItems(d.lineItems || []); }
      if (sowRes.ok) { const d = await sowRes.json(); setSows(d.sows || []); }
      if (amRes.ok) { const d = await amRes.json(); setAmendments(d.amendments || []); }
      if (sigRes.ok) { const d = await sigRes.json(); setSignoffs(d.signoffs || []); }
    } catch (e) { /* ignore */ }
  }

  async function refreshDetail() { if (selected) await loadDetailData(selected.id); }

  function closeDetail() { setSelected(null); setSelectedSow(null); }

  // ---- SOW detail ----
  async function openSowDetail(sow) {
    setSelectedSow(sow);
    try {
      const [coRes, slaRes] = await Promise.all([
        apiFetch(`/api/contracts/${selected.id}/sows/${sow.id}/change-orders`),
        apiFetch(`/api/contracts/${selected.id}/sows/${sow.id}/sla-definitions`)
      ]);
      if (coRes.ok) { const d = await coRes.json(); setChangeOrders(d.changeOrders || []); }
      if (slaRes.ok) { const d = await slaRes.json(); setSlaDefinitions(d.slaDefinitions || []); }
    } catch (e) { /* ignore */ }
  }

  async function refreshSowDetail() { if (selectedSow) await openSowDetail(selectedSow); }

  // ---- Generic sub-modal helpers ----
  function openSubModal(type, item = null) {
    resetSubDirty();
    setSubModal(type);
    setSubEditing(item);
    if (type === 'lineItem') {
      setSubForm(item ? {
        service_description: item.service_description || '', sap_module: item.sap_module || '',
        role: item.role || '', effort_days: item.effort_days || '', rate: item.rate || '',
        amount: item.amount || '', engagement_model: item.engagement_model || ''
      } : { service_description: '', sap_module: '', role: '', effort_days: '', rate: '', amount: '', engagement_model: '' });
    } else if (type === 'sow') {
      setSubForm(item ? {
        sow_number: item.sow_number || '', title: item.title || '', scope: item.scope || '',
        engagement_model: item.engagement_model || '', value: item.value || '',
        start_date: item.start_date ? item.start_date.substring(0, 10) : '',
        end_date: item.end_date ? item.end_date.substring(0, 10) : '', status: item.status || 'draft'
      } : { sow_number: '', title: '', scope: '', engagement_model: '', value: '', start_date: '', end_date: '', status: 'draft' });
    } else if (type === 'changeOrder') {
      setSubForm(item ? {
        co_number: item.co_number || '', description: item.description || '',
        additional_effort: item.additional_effort || '', additional_cost: item.additional_cost || '',
        status: item.status || 'pending'
      } : { co_number: '', description: '', additional_effort: '', additional_cost: '', status: 'pending' });
    } else if (type === 'sla') {
      setSubForm(item ? {
        priority: item.priority || '', response_hours: item.response_hours || '',
        resolution_hours: item.resolution_hours || '', penalty_pct: item.penalty_pct || ''
      } : { priority: '', response_hours: '', resolution_hours: '', penalty_pct: '' });
    } else if (type === 'amendment') {
      setSubForm(item ? {
        amendment_number: item.amendment_number || '', description: item.description || '',
        effective_date: item.effective_date ? item.effective_date.substring(0, 10) : '', status: item.status || 'draft'
      } : { amendment_number: '', description: '', effective_date: '', status: 'draft' });
    } else if (type === 'signoff') {
      setSubForm(item ? {
        signoff_by: item.signoff_by || '', signoff_date: item.signoff_date ? item.signoff_date.substring(0, 10) : '',
        status: item.status || 'pending', comments: item.comments || ''
      } : { signoff_by: '', signoff_date: '', status: 'pending', comments: '' });
    }
  }

  async function saveSubModal() {
    setSaving(true);
    let url, method, body;
    const cid = selected.id;

    if (subModal === 'lineItem') {
      body = { serviceDescription: subForm.service_description, sapModule: subForm.sap_module, role: subForm.role, effortDays: subForm.effort_days, rate: subForm.rate, amount: subForm.amount, engagementModel: subForm.engagement_model };
      url = subEditing ? `/api/contracts/${cid}/line-items/${subEditing.id}` : `/api/contracts/${cid}/line-items`;
      method = subEditing ? 'PUT' : 'POST';
    } else if (subModal === 'sow') {
      body = { sowNumber: subForm.sow_number, title: subForm.title, scope: subForm.scope, engagementModel: subForm.engagement_model, value: subForm.value, startDate: subForm.start_date, endDate: subForm.end_date, status: subForm.status };
      url = subEditing ? `/api/contracts/${cid}/sows/${subEditing.id}` : `/api/contracts/${cid}/sows`;
      method = subEditing ? 'PUT' : 'POST';
    } else if (subModal === 'changeOrder') {
      body = { coNumber: subForm.co_number, description: subForm.description, additionalEffort: subForm.additional_effort, additionalCost: subForm.additional_cost, status: subForm.status };
      url = subEditing ? `/api/contracts/${cid}/sows/${selectedSow.id}/change-orders/${subEditing.id}` : `/api/contracts/${cid}/sows/${selectedSow.id}/change-orders`;
      method = subEditing ? 'PUT' : 'POST';
    } else if (subModal === 'sla') {
      body = { priority: subForm.priority, responseHours: subForm.response_hours, resolutionHours: subForm.resolution_hours, penaltyPct: subForm.penalty_pct };
      url = subEditing ? `/api/contracts/${cid}/sows/${selectedSow.id}/sla-definitions/${subEditing.id}` : `/api/contracts/${cid}/sows/${selectedSow.id}/sla-definitions`;
      method = subEditing ? 'PUT' : 'POST';
    } else if (subModal === 'amendment') {
      body = { amendmentNumber: subForm.amendment_number, description: subForm.description, effectiveDate: subForm.effective_date, status: subForm.status };
      url = subEditing ? `/api/contracts/${cid}/amendments/${subEditing.id}` : `/api/contracts/${cid}/amendments`;
      method = subEditing ? 'PUT' : 'POST';
    } else if (subModal === 'signoff') {
      body = { entityType: 'contract', entityId: cid, signoffBy: subForm.signoff_by, signoffDate: subForm.signoff_date, status: subForm.status, comments: subForm.comments };
      url = subEditing ? `/api/contracts/signoffs/${subEditing.id}` : '/api/contracts/signoffs';
      method = subEditing ? 'PUT' : 'POST';
    }

    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) {
        resetSubDirty();
        setSubModal(null);
        refreshDetail();
        if (selectedSow && (subModal === 'changeOrder' || subModal === 'sla')) refreshSowDetail();
      }
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  async function deleteLineItem(item) {
    if (!window.confirm('Delete this line item?')) return;
    try { await apiFetch(`/api/contracts/${selected.id}/line-items/${item.id}`, { method: 'DELETE' }); refreshDetail(); } catch (e) { /* ignore */ }
  }

  async function deleteSow(sow) {
    if (!window.confirm('Delete this SOW?')) return;
    try { await apiFetch(`/api/contracts/${selected.id}/sows/${sow.id}`, { method: 'DELETE' }); refreshDetail(); } catch (e) { /* ignore */ }
  }

  async function deleteSla(sla) {
    if (!window.confirm('Delete this SLA?')) return;
    try { await apiFetch(`/api/contracts/${selected.id}/sows/${selectedSow.id}/sla-definitions/${sla.id}`, { method: 'DELETE' }); refreshSowDetail(); } catch (e) { /* ignore */ }
  }

  const custOptions = customers.map(c => ({ value: c.id, label: c.name || c.company_name || c.id }));
  const oppOptions = opportunities.map(o => ({ value: o.id, label: o.name }));
  const estOptions = estimates.map(e => ({ value: e.id, label: e.name || e.id }));
  // Projects — filter by selected customer if available so the list is relevant
  const projOptions = projects
    .filter(p => !form.customer_id || !p.customer_id || p.customer_id === form.customer_id)
    .map(p => ({ value: p.id, label: p.name || p.project_code || p.id }));

  // ---- SOW Sub-detail View ----
  if (selected && selectedSow) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: -40 }}>
          <button className="btn btn-secondary" onClick={() => { if (confirmIfDirty()) setSelectedSow(null); }}>Back to Contract</button>
          <h3 style={{ margin: 0 }}>SOW: {selectedSow.title || selectedSow.sow_number}</h3>
          <span className={`badge ${MODEL_BADGES[selectedSow.engagement_model] || 'badge-gray'}`}>{selectedSow.engagement_model || 'N/A'}</span>
        </div>

        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0 }}>Change Orders</h4>
          <button className="btn btn-primary" style={{ marginBottom: 12 }} onClick={() => openSubModal('changeOrder')}>+ Add Change Order</button>
          <DataTable
            columns={[
              { key: 'co_number', label: 'CO #', sortable: true },
              { key: 'description', label: 'Description' },
              { key: 'additional_effort', label: 'Add. Effort', sortable: true },
              { key: 'additional_cost', label: 'Add. Cost', sortable: true, render: fmtCurrency, align: 'right' },
              { key: 'status', label: 'Status', render: (v) => <span className={`badge ${STATUS_BADGES[v] || 'badge-gray'}`}>{v}</span> },
              { key: 'customer_approved', label: 'Customer OK', render: (v) => v ? 'Yes' : 'No' }
            ]}
            data={changeOrders}
            actions={[{ label: 'Edit', onClick: (co) => openSubModal('changeOrder', co) }]}
            emptyMessage="No change orders."
          />
        </div>

        <div className="card" style={{ padding: 20 }}>
          <h4 style={{ marginTop: 0 }}>SLA Definitions</h4>
          <button className="btn btn-primary" style={{ marginBottom: 12 }} onClick={() => openSubModal('sla')}>+ Add SLA</button>
          <DataTable
            columns={[
              { key: 'priority', label: 'Priority', sortable: true },
              { key: 'response_hours', label: 'Response (hrs)', sortable: true },
              { key: 'resolution_hours', label: 'Resolution (hrs)', sortable: true },
              { key: 'penalty_pct', label: 'Penalty %', sortable: true, render: (v) => v != null ? `${v}%` : '--' }
            ]}
            data={slaDefinitions}
            actions={[
              { label: 'Edit', onClick: (sla) => openSubModal('sla', sla) },
              { label: 'Delete', onClick: deleteSla }
            ]}
            emptyMessage="No SLA definitions."
          />
        </div>

        {renderSubModal()}
      </div>
    );
  }

  // ---- Contract Detail View ----
  if (selected) {
    const tabs = ['overview', 'lineItems', 'costSheet', 'sows', 'amendments', 'signoffs'];
    const tabLabels = { overview: 'Overview', lineItems: 'Line Items', costSheet: 'Cost Sheet', sows: 'SOWs', amendments: 'Amendments', signoffs: 'Signoffs' };

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: -40 }}>
          <button className="btn btn-secondary" onClick={() => { if (confirmIfDirty()) closeDetail(); }}>Back to Contracts</button>
          <h3 style={{ margin: 0 }}>{selected.contract_number}</h3>
          <span className={`badge ${STATUS_BADGES[selected.status] || 'badge-gray'}`}>{selected.status}</span>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
          {tabs.map(t => (
            <button key={t} className={`btn ${detailTab === t ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDetailTab(t)} style={{ fontSize: 13 }}>
              {tabLabels[t]}
            </button>
          ))}
        </div>

        {detailTab === 'overview' && (
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 14 }}>
              <div><strong>Customer:</strong> {selected.customer_name || '--'}</div>
              <div><strong>Opportunity:</strong> {selected.opportunity_name || '--'}</div>
              <div><strong>Estimate:</strong> {selected.estimate_name || '--'}</div>
              <div><strong>Proposal:</strong> {selected.proposal_title || '--'}</div>
              <div><strong>Project:</strong> {selected.project_name || '--'}</div>
              <div><strong>Type:</strong> {selected.type || '--'}</div>
              <div><strong>Total Value:</strong> {fmtCurrency(selected.total_value)}</div>
              <div><strong>Negotiated Value:</strong> {fmtCurrency(selected.negotiated_value)}</div>
              <div><strong>Start Date:</strong> {fmtDate(selected.start_date)}</div>
              <div><strong>End Date:</strong> {fmtDate(selected.end_date)}</div>
              <div><strong>Currency:</strong> {selected.currency || 'INR'}</div>
              <div><strong>Signed Date:</strong> {fmtDate(selected.signed_date)}</div>
              <div><strong>PO Number:</strong> {selected.po_number || '--'}</div>
              <div><strong>Payment Terms:</strong> {selected.payment_terms || '--'}</div>
              <div style={{ gridColumn: '1 / -1' }}><strong>Billing Terms:</strong> {selected.billing_terms || '--'}</div>
              <div><strong>Document URL:</strong> {selected.document_url ? <a href={selected.document_url} target="_blank" rel="noreferrer" style={{ color: '#6366F1' }}>{selected.document_url} &#8599;</a> : '--'}</div>
              <div><strong>PO Document:</strong> {selected.po_document_url ? <a href={selected.po_document_url} target="_blank" rel="noreferrer" style={{ color: '#6366F1' }}>{selected.po_document_url} &#8599;</a> : '--'}</div>
              <div><strong>Contract Document:</strong> {selected.contract_document_url ? <a href={selected.contract_document_url} target="_blank" rel="noreferrer" style={{ color: '#6366F1' }}>{selected.contract_document_url} &#8599;</a> : '--'}</div>
            </div>
            {selected.project_id && (
              <div style={{ marginTop: 16 }}>
                <button className="btn btn-primary" onClick={() => window.location.hash = '#/resources'}>
                  Allocate Resources
                </button>
              </div>
            )}
          </div>
        )}

        {detailTab === 'lineItems' && (
          <div className="card" style={{ padding: 20 }}>
            <button className="btn btn-primary" style={{ marginBottom: 12 }} onClick={() => openSubModal('lineItem')}>+ Add Line Item</button>
            <DataTable
              columns={[
                { key: 'service_description', label: 'Service' },
                { key: 'sap_module', label: 'SAP Module' },
                { key: 'role', label: 'Role' },
                { key: 'effort_days', label: 'Days', type: 'number' },
                { key: 'rate', label: 'Rate', render: fmtCurrency, align: 'right' },
                { key: 'amount', label: 'Amount', render: fmtCurrency, align: 'right' },
                { key: 'engagement_model', label: 'Model', render: (v) => v ? <span className={`badge ${MODEL_BADGES[v] || 'badge-gray'}`}>{v}</span> : '--' }
              ]}
              data={lineItems}
              actions={[
                { label: 'Edit', onClick: (li) => openSubModal('lineItem', li) },
                { label: 'Delete', onClick: deleteLineItem }
              ]}
              emptyMessage="No line items."
            />
          </div>
        )}

        {detailTab === 'costSheet' && (
          <div className="card" style={{ padding: 20 }}>
            {selected.estimate_id ? (
              <CostSheet estimateId={selected.estimate_id} estimateName={`${selected.contract_number} Cost Sheet`} />
            ) : (
              <div style={{ textAlign: 'center', color: '#94A3B8', padding: 40 }}>No estimate linked to this contract. Link an estimate to view the cost sheet.</div>
            )}
          </div>
        )}

        {detailTab === 'sows' && (
          <div className="card" style={{ padding: 20 }}>
            <button className="btn btn-primary" style={{ marginBottom: 12 }} onClick={() => openSubModal('sow')}>+ Add SOW</button>
            <DataTable
              columns={[
                { key: 'sow_number', label: 'SOW #', sortable: true },
                { key: 'title', label: 'Title', sortable: true },
                { key: 'engagement_model', label: 'Model', render: (v) => v ? <span className={`badge ${MODEL_BADGES[v] || 'badge-gray'}`}>{v}</span> : '--' },
                { key: 'value', label: 'Value', render: fmtCurrency, align: 'right' },
                { key: 'start_date', label: 'Start', render: fmtDate },
                { key: 'end_date', label: 'End', render: fmtDate },
                { key: 'status', label: 'Status', render: (v) => <span className={`badge ${STATUS_BADGES[v] || 'badge-gray'}`}>{v}</span> }
              ]}
              data={sows}
              actions={[
                { label: 'Details', onClick: openSowDetail },
                { label: 'Edit', onClick: (s) => openSubModal('sow', s) },
                { label: 'Delete', onClick: deleteSow }
              ]}
              emptyMessage="No SOWs."
            />
          </div>
        )}

        {detailTab === 'amendments' && (
          <div className="card" style={{ padding: 20 }}>
            <button className="btn btn-primary" style={{ marginBottom: 12 }} onClick={() => openSubModal('amendment')}>+ Add Amendment</button>
            <DataTable
              columns={[
                { key: 'amendment_number', label: 'Amendment #', sortable: true },
                { key: 'description', label: 'Description' },
                { key: 'effective_date', label: 'Effective', render: fmtDate },
                { key: 'status', label: 'Status', render: (v) => <span className={`badge ${STATUS_BADGES[v] || 'badge-gray'}`}>{v}</span> }
              ]}
              data={amendments}
              actions={[{ label: 'Edit', onClick: (a) => openSubModal('amendment', a) }]}
              emptyMessage="No amendments."
            />
          </div>
        )}

        {detailTab === 'signoffs' && (
          <div className="card" style={{ padding: 20 }}>
            <button className="btn btn-primary" style={{ marginBottom: 12 }} onClick={() => openSubModal('signoff')}>+ Add Signoff</button>
            <DataTable
              columns={[
                { key: 'signoff_by', label: 'Signed By', sortable: true },
                { key: 'signoff_date', label: 'Date', render: fmtDate },
                { key: 'status', label: 'Status', render: (v) => <span className={`badge ${STATUS_BADGES[v] || 'badge-gray'}`}>{v}</span> },
                { key: 'comments', label: 'Comments' }
              ]}
              data={signoffs}
              actions={[{ label: 'Edit', onClick: (s) => openSubModal('signoff', s) }]}
              emptyMessage="No signoffs."
            />
          </div>
        )}

        {renderSubModal()}
      </div>
    );
  }

  // ---- Sub-modal renderer ----
  function renderSubModal() {
    if (!subModal) return null;

    const titles = {
      lineItem: subEditing ? 'Edit Line Item' : 'New Line Item',
      sow: subEditing ? 'Edit SOW' : 'New SOW',
      changeOrder: subEditing ? 'Edit Change Order' : 'New Change Order',
      sla: subEditing ? 'Edit SLA' : 'New SLA',
      amendment: subEditing ? 'Edit Amendment' : 'New Amendment',
      signoff: subEditing ? 'Edit Signoff' : 'New Signoff'
    };

    const sf = (n, v) => { setSubForm(p => ({ ...p, [n]: v })); markSubDirty(); };

    return (
      <Modal title={titles[subModal]} isOpen={true} onClose={() => setSubModal(null)} size="lg" dirty={subDirty}>
        {subModal === 'lineItem' && (<>
          <FormField label="Service Description" name="service_description" value={subForm.service_description} onChange={sf} />
          <div className="form-row">
            <FormField label="SAP Module" name="sap_module" value={subForm.sap_module} onChange={sf} />
            <FormField
              label="Role"
              name="role"
              type="select"
              value={subForm.role}
              onChange={sf}
              options={ROLE_OPTIONS}
              placeholder="Select role..."
            />
          </div>
          <div className="form-row">
            <FormField label="Effort Days" name="effort_days" type="number" value={subForm.effort_days} onChange={sf} min="0" />
            <FormField label="Rate" name="rate" type="number" value={subForm.rate} onChange={sf} min="0" />
          </div>
          <div className="form-row">
            <FormField label="Amount" name="amount" type="number" value={subForm.amount} onChange={sf} min="0" />
            <FormField label="Engagement Model" name="engagement_model" type="select" value={subForm.engagement_model} onChange={sf} options={ENGAGEMENT_MODELS} />
          </div>
        </>)}

        {subModal === 'sow' && (<>
          <div className="form-row">
            <FormField label="SOW Number" name="sow_number" value={subForm.sow_number} onChange={sf} />
            <FormField label="Title" name="title" value={subForm.title} onChange={sf} />
          </div>
          <FormField label="Scope" name="scope" type="textarea" value={subForm.scope} onChange={sf} />
          <div className="form-row">
            <FormField label="Engagement Model" name="engagement_model" type="select" value={subForm.engagement_model} onChange={sf} options={ENGAGEMENT_MODELS} />
            <FormField label="Value" name="value" type="number" value={subForm.value} onChange={sf} min="0" />
          </div>
          <div className="form-row">
            <FormField label="Start Date" name="start_date" type="date" value={subForm.start_date} onChange={sf} />
            <FormField label="End Date" name="end_date" type="date" value={subForm.end_date} onChange={sf} />
          </div>
          <FormField label="Status" name="status" type="select" value={subForm.status} onChange={sf} options={SOW_STATUSES} />
        </>)}

        {subModal === 'changeOrder' && (<>
          <div className="form-row">
            <FormField label="CO Number" name="co_number" value={subForm.co_number} onChange={sf} />
            <FormField label="Status" name="status" type="select" value={subForm.status} onChange={sf} options={CO_STATUSES} />
          </div>
          <FormField label="Description" name="description" type="textarea" value={subForm.description} onChange={sf} />
          <div className="form-row">
            <FormField label="Additional Effort (days)" name="additional_effort" type="number" value={subForm.additional_effort} onChange={sf} min="0" />
            <FormField label="Additional Cost" name="additional_cost" type="number" value={subForm.additional_cost} onChange={sf} min="0" />
          </div>
        </>)}

        {subModal === 'sla' && (<>
          <div className="form-row">
            <FormField label="Priority" name="priority" type="select" value={subForm.priority} onChange={sf} options={SLA_PRIORITIES} />
            <FormField label="Penalty %" name="penalty_pct" type="number" value={subForm.penalty_pct} onChange={sf} min="0" max="100" />
          </div>
          <div className="form-row">
            <FormField label="Response Hours" name="response_hours" type="number" value={subForm.response_hours} onChange={sf} min="0" />
            <FormField label="Resolution Hours" name="resolution_hours" type="number" value={subForm.resolution_hours} onChange={sf} min="0" />
          </div>
        </>)}

        {subModal === 'amendment' && (<>
          <div className="form-row">
            <FormField label="Amendment Number" name="amendment_number" value={subForm.amendment_number} onChange={sf} />
            <FormField label="Status" name="status" type="select" value={subForm.status} onChange={sf} options={AMENDMENT_STATUSES} />
          </div>
          <FormField label="Description" name="description" type="textarea" value={subForm.description} onChange={sf} />
          <FormField label="Effective Date" name="effective_date" type="date" value={subForm.effective_date} onChange={sf} />
        </>)}

        {subModal === 'signoff' && (<>
          <div className="form-row">
            <FormField label="Signed By" name="signoff_by" value={subForm.signoff_by} onChange={sf} />
            <FormField label="Date" name="signoff_date" type="date" value={subForm.signoff_date} onChange={sf} />
          </div>
          <div className="form-row">
            <FormField label="Status" name="status" type="select" value={subForm.status} onChange={sf} options={SIGNOFF_STATUSES} />
            <div></div>
          </div>
          <FormField label="Comments" name="comments" type="textarea" value={subForm.comments} onChange={sf} />
        </>)}

        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setSubModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveSubModal} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </Modal>
    );
  }

  // ---- Main List View ----
  const columns = [
    { key: 'contract_number', label: 'Contract #', sortable: true },
    { key: 'customer_name', label: 'Customer', sortable: true, render: (v) => v || '--' },
    { key: 'type', label: 'Type', sortable: true, render: (v) => v || '--' },
    { key: 'total_value', label: 'Value', sortable: true, render: fmtCurrency, align: 'right' },
    { key: 'start_date', label: 'Start', sortable: true, render: fmtDate },
    { key: 'end_date', label: 'End', sortable: true, render: fmtDate },
    { key: 'status', label: 'Status', sortable: true, render: (v) => <span className={`badge ${STATUS_BADGES[v] || 'badge-gray'}`}>{v}</span> }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, marginTop: -40 }}>
        <button className="btn btn-primary" onClick={openCreate}>+ New Contract</button>
      </div>

      <DataTable
        columns={columns}
        data={contracts}
        loading={loading}
        actions={[
          { label: 'View', onClick: openViewPage },
          { label: 'Edit', onClick: openEditPage },
          { label: 'Delete', onClick: handleDelete }
        ]}
        emptyMessage="No contracts found. Create your first contract."
      />

      <Modal title={editing ? 'Edit Contract' : 'New Contract'} isOpen={modalOpen} onClose={() => setModalOpen(false)} size="lg" dirty={dirty}>
        {errors._general && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{errors._general}</div>}
        <div className="form-row">
          <FormField label="Contract Number" name="contract_number" value={form.contract_number} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} error={errors.contract_number} required />
          <FormField label="Type" name="type" type="select" value={form.type} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} options={CONTRACT_TYPES} />
        </div>
        <div className="form-row">
          <div style={{ flex: 1 }}>
            <SearchableSelect label="Customer" value={form.customer_id} onChange={v => { setForm(p => ({ ...p, customer_id: v })); markDirty(); }} options={custOptions} placeholder="Select customer..." />
          </div>
          <FormField label="Opportunity" name="opportunity_id" type="select" value={form.opportunity_id} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} options={oppOptions} placeholder="Select opportunity..." />
        </div>
        <div className="form-row">
          <FormField label="Estimate" name="estimate_id" type="select" value={form.estimate_id} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} options={estOptions} placeholder="Select estimate..." />
          <div>
            <FormField
              label="Project"
              name="project_id"
              type="select"
              value={form.project_id}
              onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }}
              options={projOptions}
              placeholder={projOptions.length === 0 ? 'No projects available' : 'Select project...'}
            />
            {projOptions.length === 0 && (
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                No projects {form.customer_id ? 'for the selected customer' : ''} yet. Create one under <strong>Delivery → Projects</strong>.
              </div>
            )}
          </div>
        </div>
        <div className="form-row">
          <FormField label="Proposal" name="proposal_id" value={form.proposal_id} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} placeholder="Proposal ID..." />
          <FormField label="PO Number" name="po_number" value={form.po_number} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        </div>
        <div className="form-row">
          <FormField label="Total Value" name="total_value" type="number" value={form.total_value} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} min="0" />
          <FormField label="Negotiated Value" name="negotiated_value" type="number" value={form.negotiated_value} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} min="0" />
        </div>
        <div className="form-row">
          <FormField label="Currency" name="currency" value={form.currency} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="Payment Terms" name="payment_terms" value={form.payment_terms} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} placeholder="e.g. Net 30" />
        </div>
        <div className="form-row">
          <FormField label="Start Date" name="start_date" type="date" value={form.start_date} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="End Date" name="end_date" type="date" value={form.end_date} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        </div>
        <div className="form-row">
          <FormField label="Status" name="status" type="select" value={form.status} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} options={CONTRACT_STATUSES} />
          <FormField label="Document URL" name="document_url" value={form.document_url} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        </div>
        <div className="form-row">
          <FormField label="PO Document URL" name="po_document_url" value={form.po_document_url} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="Contract Document URL" name="contract_document_url" value={form.contract_document_url} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        </div>
        <FormField label="Billing Terms" name="billing_terms" type="textarea" value={form.billing_terms} onChange={(n, v) => { setForm(p => ({ ...p, [n]: v })); markDirty(); }} />

        {/* Line Items + Sub-Items editor (Phase 2) — only when editing an existing contract */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {editing ? (
            <ContractLineItemsEditor
              contractId={editing.id}
              contractStart={form.start_date}
              contractEnd={form.end_date}
            />
          ) : (
            <div style={{
              padding: 14, textAlign: 'center', color: '#94A3B8',
              border: '1px dashed rgba(255,255,255,0.10)', borderRadius: 8, fontSize: 12.5,
            }}>
              Save the contract first to start adding line items and sub-items.
            </div>
          )}
        </div>

        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Create')}</button>
        </div>
      </Modal>
    </div>
  );
}
